import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Users, Gift, TrendingUp, Percent, Share2, 
  Copy, QrCode, Trophy, ArrowUp, Sparkles,
  Calendar, Tag, BarChart3
} from 'lucide-react';
import ReferralProgram from './ReferralProgram';
import PromoCodes from './PromoCodes';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MarketingDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/marketing/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="marketing-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-purple-600" />
            Marketing Hub
          </h1>
          <p className="text-gray-600 mt-1">
            Grow your membership base with referrals and promotions
          </p>
        </div>
      </div>

      {/* Quick Stats */}
      {analytics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Referrers</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.referrals.total_active_codes}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Referrals This Month</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.referrals.completed_this_month}
                  </p>
                  {analytics.referrals.pending > 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      +{analytics.referrals.pending} pending
                    </p>
                  )}
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Promos</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.promos.active_codes}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Tag className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Promo Uses This Month</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {analytics.promos.uses_this_month}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                  <Percent className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-gray-100 p-1">
          <TabsTrigger 
            value="overview" 
            className="data-[state=active]:bg-white"
            data-testid="tab-overview"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger 
            value="referrals" 
            className="data-[state=active]:bg-white"
            data-testid="tab-referrals"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Referral Program
          </TabsTrigger>
          <TabsTrigger 
            value="promos" 
            className="data-[state=active]:bg-white"
            data-testid="tab-promos"
          >
            <Gift className="h-4 w-4 mr-2" />
            Promo Codes
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Overview Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Referral Program Card */}
            <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('referrals')}>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
                    <Share2 className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Referral Program</CardTitle>
                    <CardDescription>Turn members into ambassadors</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Let your members share unique referral codes and earn rewards when friends join.
                  </p>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-green-600">
                      <ArrowUp className="h-4 w-4" />
                      <span>+25-35% new members</span>
                    </div>
                  </div>
                  <Button variant="outline" className="w-full mt-2">
                    Manage Referrals →
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Promo Codes Card */}
            <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('promos')}>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                    <Gift className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Promotions & Coupons</CardTitle>
                    <CardDescription>Create irresistible offers</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Launch time-limited discounts, seasonal promotions, and welcome offers.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="secondary">% Discounts</Badge>
                    <Badge variant="secondary">Fixed Amount</Badge>
                    <Badge variant="secondary">Free Month</Badge>
                  </div>
                  <Button variant="outline" className="w-full mt-2">
                    Manage Promos →
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button 
                  variant="outline" 
                  className="h-auto py-4 flex flex-col gap-2"
                  onClick={() => setActiveTab('promos')}
                >
                  <Tag className="h-5 w-5 text-blue-600" />
                  <span className="text-sm">Create Promo</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-auto py-4 flex flex-col gap-2"
                  onClick={() => setActiveTab('referrals')}
                >
                  <Trophy className="h-5 w-5 text-amber-600" />
                  <span className="text-sm">View Leaderboard</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-auto py-4 flex flex-col gap-2"
                  onClick={() => setActiveTab('promos')}
                >
                  <Calendar className="h-5 w-5 text-green-600" />
                  <span className="text-sm">Flash Sale</span>
                </Button>
                <Button 
                  variant="outline" 
                  className="h-auto py-4 flex flex-col gap-2"
                  onClick={() => setActiveTab('referrals')}
                >
                  <QrCode className="h-5 w-5 text-purple-600" />
                  <span className="text-sm">Share QR Code</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tips Section */}
          <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
            <CardHeader>
              <CardTitle className="text-lg text-purple-900">💡 Marketing Tips</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-purple-800">
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  <span><strong>Referral programs</strong> typically bring 25-35% of new customers at lower acquisition cost</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  <span><strong>Flash sales</strong> (24-48hr) create urgency and can boost conversions by 50%+</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  <span><strong>Welcome discounts</strong> (10-15%) help convert hesitant prospects</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500">•</span>
                  <span><strong>QR codes</strong> on printed materials make it easy for walk-ins to sign up</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="referrals">
          <ReferralProgram onRefresh={loadAnalytics} />
        </TabsContent>

        <TabsContent value="promos">
          <PromoCodes onRefresh={loadAnalytics} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MarketingDashboard;
